/**
 * Bridges the imperative `PreviewEngine` and React. The engine owns the clock; this store mirrors
 * the current frame / playing state for UI that needs it (timecode, playhead).
 */
import { create } from 'zustand';
import type { PreviewEngine } from '@ve/engine';
import { activeSourceEngine, closeSourcePreview } from './source-preview.ts';

interface PlaybackState {
  frame: number;
  playing: boolean;
  engine: PreviewEngine | null;
  attach(engine: PreviewEngine): () => void;
}

export const usePlayback = create<PlaybackState>()((set, get) => ({
  frame: 0,
  playing: false,
  engine: null,
  attach(engine) {
    set({ engine, frame: engine.frame, playing: engine.playing });
    const unsub = engine.subscribe(({ frame, playing }) => {
      const s = get();
      if (s.frame !== frame || s.playing !== playing) set({ frame, playing });
    });
    return () => {
      unsub();
      if (get().engine === engine) set({ engine: null });
    };
  },
}));

/**
 * Imperative transport helpers usable from anywhere (shortcuts, buttons).
 *
 * `frame` and `seek` always mean the timeline: edits happen at the timeline playhead, and seeking
 * it (ruler, markers, keyframes) leaves a library source preview. Playback commands (`toggle`,
 * `step`, `seekViewer`) drive whatever the viewer shows — the source preview if one is open.
 */
export const transport = {
  get frame(): number {
    return usePlayback.getState().engine?.frame ?? usePlayback.getState().frame;
  },
  seek(frame: number) {
    closeSourcePreview();
    const engine = usePlayback.getState().engine;
    const f = Math.max(0, Math.round(frame));
    if (engine) engine.seek(f);
    else usePlayback.setState({ frame: f });
  },
  /** Seeks what the viewer shows; `Infinity` goes to the end. */
  seekViewer(frame: number) {
    const source = activeSourceEngine();
    if (!source) return transport.seek(frame);
    source.seek(Math.max(0, Math.round(Number.isFinite(frame) ? frame : source.duration)));
  },
  step(delta: number) {
    const source = activeSourceEngine();
    if (source) {
      source.pause();
      source.seek(Math.max(0, source.frame + delta));
      return;
    }
    transport.pause();
    transport.seek(transport.frame + delta);
  },
  play() {
    (activeSourceEngine() ?? usePlayback.getState().engine)?.play();
  },
  pause() {
    activeSourceEngine()?.pause();
    usePlayback.getState().engine?.pause();
  },
  toggle() {
    (activeSourceEngine() ?? usePlayback.getState().engine)?.togglePlay();
  },
};
